/**
 * Dashboard primitives (A00 disciplines 10 + 4 + 15).
 *
 * One set of building blocks every dashboard composes from, so a page header, a section title and a stat
 * tile are IDENTICAL across Home, Wallet, Portfolio, Earnings, Merchant, Business, Lender and Analytics,
 * rather than seven near-misses. Type is chosen by role (`text-page-title`, `text-section`), and a
 * missing value renders as "-", never a misleading 0.
 */

import type { ReactNode } from 'react';

/** A page's H1 and optional subtitle, with optional right-aligned actions. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-page-title text-ink">{title}</h1>
        {subtitle ? <p className="mt-0.5 text-sm text-ink-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/** A section heading, with an optional trailing action (a "See all" link). */
export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <h2 className="text-section text-ink">{children}</h2>
      {action ?? null}
    </div>
  );
}

/**
 * A stat tile. A null / undefined / empty value renders "-", so a dead field is a visible gap rather
 * than a wrong number somebody acts on (never a misleading zero).
 */
export function StatTile({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string | number | null | undefined;
  sub?: string;
  accent?: boolean;
}) {
  const shown = value === null || value === undefined || value === '' ? '-' : String(value);
  return (
    <div className="rounded-card border border-border bg-surface p-3 shadow-card">
      <p className="text-micro text-ink-subtle">{label}</p>
      <p className={`tabular mt-0.5 text-base font-semibold ${accent ? 'text-brand' : 'text-ink'}`}>
        {shown}
      </p>
      {sub ? <p className="truncate text-micro text-ink-subtle">{sub}</p> : null}
    </div>
  );
}
